export type CheckoutMode = "edit" | "readonly";

export type CheckoutModeContext = {
  confirmedOrder?: { uuid?: string | null; orderCompleted?: boolean };
  cartItemsConfirmed: unknown[];
  paymentMethod?: { uuid?: string | null } | null;
  orderDelivery?: { uuid?: string | null } | null;
  deliveryConfirmed?: { uuid?: string | null } | null;
  deliveryRequired?: boolean;
  paymentRequired?: boolean;
  currentPageType?: string | null;
};

function hasConfirmedOrderUuid(
  confirmedOrder?: { uuid?: string | null; orderCompleted?: boolean }
): boolean {
  return !!(confirmedOrder?.uuid);
}

function hasCompletedOrder(
  confirmedOrder?: { uuid?: string | null; orderCompleted?: boolean }
): boolean {
  return confirmedOrder?.orderCompleted === true;
}

function hasDeliverySelected(ctx: CheckoutModeContext): boolean {
  return !!(ctx.orderDelivery?.uuid ?? ctx.deliveryConfirmed?.uuid);
}

function hasPaymentSelected(ctx: CheckoutModeContext): boolean {
  return !!ctx.paymentMethod?.uuid;
}

function hasSoftReadonlyState(ctx: CheckoutModeContext): boolean {
  const deliveryReady =
    ctx.deliveryRequired === false ? true : hasDeliverySelected(ctx);
  const paymentReady =
    ctx.paymentRequired === false ? true : hasPaymentSelected(ctx);
  return (
    ctx.cartItemsConfirmed.length > 0 &&
    paymentReady &&
    deliveryReady
  );
}

export function getCheckoutMode(ctx: CheckoutModeContext): CheckoutMode {
  if (!hasConfirmedOrderUuid(ctx.confirmedOrder)) {
    return "edit";
  }

  // Redirect payment methods return with a fresh app state where paymentMethod
  // may be empty. A completed order should still stay in readonly mode.
  if (hasCompletedOrder(ctx.confirmedOrder)) {
    return "readonly";
  }

  if (ctx.currentPageType === "order-complete") {
    return "readonly";
  }

  if (hasSoftReadonlyState(ctx)) {
    return "readonly";
  }

  return "edit";
}

export function isCheckoutReadOnly(mode: CheckoutMode): boolean {
  return mode === "readonly";
}

/** Show success header only on completed readonly checkout. */
export function showCheckoutSuccessHeader(
  mode: CheckoutMode,
  confirmedOrder?: { uuid?: string | null }
): boolean {
  return isCheckoutReadOnly(mode) && hasConfirmedOrderUuid(confirmedOrder);
}
